import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button } from '@/components/shadcn/button';
import PickleballEventHeader from '@/components/community/pickleball/PickleballEventHeader';
import PickleballStatistikContent from '@/components/community/pickleball/PickleballStatistikContent';
import PickleballExportCanvas from '@/components/community/pickleball/PickleballExportCanvas';
import PickleballStatistikOverlays from '@/components/community/pickleball/PickleballStatistikOverlays';
import type { PickleballStatistikTab } from '@/components/community/pickleball/PickleballStatistikTabs';
import type { FlatPickleballGame } from '@/components/community/pickleball/pickleballListUtils';
import type { PickleballGameListItem } from '@/services/pickleballService';
import { usePickleballStatistik } from '@/pages/community/usePickleballStatistik';
import { hostPaths } from '@/utils/hostPaths';
import { clientPaths, isCustomerAppPath } from '@/utils/clientPaths';
import { downloadOrShareBlob } from '@/utils/downloadBlob';

export default function PickleballStatistikPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isClient = isCustomerAppPath(location.pathname);
  const [tab, setTab] = useState<PickleballStatistikTab>('games');
  const [feedback, setFeedback] = useState<{ icon: string; title: string; message: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FlatPickleballGame | null>(null);
  const [lineup, setLineup] = useState<{ game: PickleballGameListItem; gameNumber: number } | null>(null);
  const [score, setScore] = useState<FlatPickleballGame | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const data = usePickleballStatistik(slug, isClient);

  if (data.loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!data.event || !slug) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-gray-600">{data.errorMessage || 'Event tidak ditemukan'}</p>
        <Button className="mt-4" onClick={() => navigate(isClient ? '/event' : hostPaths.mabar.list)}>
          Kembali
        </Button>
      </div>
    );
  }

  const handleShare = async () => {
    const url = `${window.location.origin}${clientPaths.event.statistik(slug)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // clipboard may be unavailable
    }
    setFeedback({
      icon: 'mdi:share-variant-outline',
      title: 'Bagikan Statistik',
      message: `Tautan statistik ${data?.event?.name || 'event'} disalin. Bagikan ke grup WhatsApp atau media sosial komunitas.`,
    });
  };

  const handleDownload = async () => {
    if (!exportRef.current) return;
    const canvas = await html2canvas(exportRef.current, { backgroundColor: '#F4F6F8', scale: 2 });
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return;
    await downloadOrShareBlob(blob, `statistik-pickleball-${slug}.png`, { title: data?.event?.name || 'event' });
    setFeedback({
      icon: 'mdi:download',
      title: 'Download Statistik',
      message: `Klasemen ${data?.event?.name || 'event'} diunduh sebagai gambar untuk dibagikan.`,
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-4">
      <PickleballEventHeader event={data.event} />
      <PickleballStatistikContent
        tab={tab}
        onTabChange={setTab}
        canEdit={data.canEdit}
        standings={data.standings}
        flatGames={data.flatGames}
        onTambahGame={() => setManualOpen(true)}
        onEditScore={setScore}
        onEditPlayer={(game, gameNumber) => setLineup({ game, gameNumber })}
        onDelete={setPendingDelete}
        onShare={handleShare}
        onDownload={handleDownload}
      />
      <PickleballExportCanvas eventName={data.event.name} standings={data.standings} canvasRef={exportRef} />
      <PickleballStatistikOverlays
        slug={slug}
        service={data.service}
        participants={data.participants}
        sessions={data.sessions}
        loadData={data.loadData}
        feedback={feedback}
        setFeedback={setFeedback}
        pendingDelete={pendingDelete}
        setPendingDelete={setPendingDelete}
        lineup={lineup}
        setLineup={setLineup}
        score={score}
        setScore={setScore}
        manualOpen={manualOpen}
        setManualOpen={setManualOpen}
        saving={saving}
        setSaving={setSaving}
      />
    </div>
  );
}
